import React from "react";
import { PanelHelp } from "./QuickStart";
import { toast } from "sonner";
import { Archive, Pin, X } from "lucide-react";
import { StrikeChip, StrikeHint, useTicketStrikes } from "./StrikeChip";
import { EditableTargetBadge } from "./EditableTargetBadge";
import { leftMetrics, rightMetrics } from "../lib/excelHelpers";
import { saveTicket } from "../lib/ticketHistory";

// Optimal Daily Double ticket:
//   Leg 1 : top N by DDPO rank (S values of the handicapped leftRows)
//   Leg 2 : top M by SLPO rank (S2 values of the handicapped rightRows)
// Structure selector (below) picks (N, M). "Must-Include" locks pin
// specific posts into a leg; remaining slots are then filled by the next
// ranked entries.
const STRUCTURES = [
  { key: "2x5", label: "2 × 5", leg1: 2, leg2: 5 },
  { key: "2x4", label: "2 × 4", leg1: 2, leg2: 4 },
  { key: "3x3", label: "3 × 3", leg1: 3, leg2: 3 },
  { key: "3x4", label: "3 × 4", leg1: 3, leg2: 4 },
];
const DEFAULT_STRUCTURE = "2x5";

// Valid = has an IQ Power (excludes $1,000 "No Rank" and unrankable rows).
const validLeftRanked = (leftRows) => {
  const m = leftMetrics(leftRows);
  return leftRows.filter((_, i) => m[i].iq !== null);
};

const validRightRanked = (rightRows) => {
  const m = rightMetrics(rightRows);
  return rightRows.filter((_, i) => m[i].iq !== null);
};

// Deprecated: TargetBadge is now the shared EditableTargetBadge component.

const LockPin = ({ locked }) =>
  locked ? (
      <span
        className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center rounded-full"
        style={{
          background: "#facc15",
          color: "#0a0a0a",
          width: 16,
          height: 16,
          boxShadow: "0 0 0 2px currentColor",
        }}
        aria-hidden="true"
      >
        <Pin size={9} strokeWidth={3} />
      </span>
  ) : null;

const Line = ({ label, sublabel, horses, locks, tone, prefix, strikes, slot }) => {
  const styles = {
    lime: { bg: "#84cc16", accent: "#0a0a0a" },
    cream: { bg: "#f5ecd6", accent: "#0a0a0a" },
  };
  const s = styles[tone];
  const isLocked = (n) => locks instanceof Set && locks.has(Number(n));
  return (
    <div
      data-testid={`line-${prefix}`}
      className="flex items-center gap-4 px-5 py-4 rounded-md"
      style={{ background: s.bg }}
    >
      <div className="min-w-[110px]">
        <div
          className="text-[11px] uppercase tracking-[0.22em] font-bold"
          style={{ color: s.accent, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
        >
          {label}
        </div>
        <div
          className="text-[10px] uppercase tracking-[0.16em] mt-0.5"
          style={{ color: s.accent, opacity: 0.65, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
        >
          {sublabel}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {horses.map((n, i) => (
          <StrikeChip
            key={`${n}-${i}`}
            n={n}
            testId={`${prefix}-${i}`}
            struck={strikes.isStruck(slot, n)}
            onToggle={() => strikes.toggle(slot, n)}
            badge={<LockPin locked={isLocked(n)} />}
          />
        ))}
      </div>
    </div>
  );
};

const WithDivider = () => (
  <div
    data-testid="dd-with"
    className="text-center text-stone-500 text-[11px] uppercase tracking-[0.4em] py-1"
    style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
  >
    with
  </div>
);

const BaseSelector = ({ value, onChange }) => {
  const options = [0.5, 1, 2];
  return (
    <div className="flex items-center gap-2" data-testid="base-selector">
      <span
        className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mr-1"
        style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
      >
        Base
      </span>
      {options.map((v) => {
        const active = value === v;
        return (
          <button
            key={v}
            data-testid={`base-${v}`}
            onClick={() => onChange(v)}
            className={`px-3 h-8 rounded-md text-[11px] font-bold tracking-[0.14em] uppercase transition-colors border ${
              active
                ? "bg-[#84cc16] text-[#0a0a0a] border-[#365314]"
                : "bg-transparent text-stone-300 border-stone-700 hover:border-stone-500"
            }`}
            style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
          >
            ${v.toFixed(2)}
          </button>
        );
      })}
    </div>
  );
};

const fmtCost = (n) => {
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(2);
};

export const OptimalTicket = ({
  leftRows,
  rightRows,
  handicapped,
  trackName,
  trackSlug,
  race,
  date,
  leftElims,
  leg2Locks,
  tracks,
  onEditTarget,
}) => {
  const [base, setBase] = React.useState(1);
  const [structureKey, setStructureKey] = React.useState(DEFAULT_STRUCTURE);
  const structure =
    STRUCTURES.find((s) => s.key === structureKey) || STRUCTURES[0];

  const elimSet = leftElims instanceof Set ? leftElims : null;
  const isElim = (n) => !!(elimSet && elimSet.has(Number(n)));

  const leftValid = validLeftRanked(leftRows).filter((r) => !isElim(r.S));
  const rightValid = validRightRanked(rightRows);
  const totalValid = leftValid.length + rightValid.length;

  // Leg 1: top-N by IQ Power rank, skipping eliminated horses.
  const leg1 = leftRows
    .filter((r) => r.RANK && !isElim(r.S))
    .slice(0, structure.leg1)
    .map((r) => Number(r.S));

  // Leg 2: locked posts first (in their current row order), then
  // next-ranked non-locked posts, truncated to the structure size.
  const buildLeg2 = (rows, size) => {
    const locksSet = leg2Locks instanceof Set ? leg2Locks : null;
    const has = (n) => !!(locksSet && locksSet.has(Number(n)));
    const locked = rows
      .map((r) => Number(r.S2))
      .filter((n) => Number.isFinite(n) && has(n));
    const ranked = validRightRanked(rows)
      .map((r) => Number(r.S2))
      .filter((n) => Number.isFinite(n) && !has(n));
    const seen = new Set();
    const merged = [];
    for (const n of [...locked, ...ranked]) {
      if (seen.has(n)) continue;
      seen.add(n);
      merged.push(n);
      if (merged.length >= size) break;
    }
    return merged;
  };
  const leg2 = buildLeg2(rightRows, structure.leg2);
  const strikes = useTicketStrikes(`${trackSlug}|${race}`);
  const leg1Bet = strikes.keep("leg1", leg1);
  const leg2Bet = strikes.keep("leg2", leg2);
  const struckCount =
    strikes.countIn("leg1", leg1) + strikes.countIn("leg2", leg2);

  const leg2LockCount = leg2Locks instanceof Set ? leg2Locks.size : 0;
  const tooManyLeg2Locks = leg2LockCount > structure.leg2;

  const tooFew = leg1.length < structure.leg1 || leg2.length < structure.leg2;
  const tooManyLeft = leftValid.length > 10;
  const tooManyRight = rightValid.length > 10;
  const tooLarge = tooManyLeft || tooManyRight;
  const combos = leg1Bet.length * leg2Bet.length;
  const cost = fmtCost(combos * base);

  const leg1Key = leg1.join(",");
  const leg2Key = leg2.join(",");

  const archive = () => {
    if (tooLarge || tooFew || combos === 0) return;
    saveTicket({
      kind: "DD",
      track: trackName || "",
      track_slug: trackSlug || "",
      race: race || "",
      date: date || "",
      base,
      combos,
      cost,
      structure: structure.key,
      leg2_locked: Array.from(leg2Locks || []).filter((n) => leg2Bet.includes(n)),
      left_elims: Array.from(leftElims || []),
      ticket_strikes: {
        leg1: leg1.filter((n) => !leg1Bet.includes(n)),
        leg2: leg2.filter((n) => !leg2Bet.includes(n)),
      },
      horses: [
        { label: "1st Leg (IQ Power)", posts: leg1Bet },
        { label: "2nd Leg (IQ Power)", posts: leg2Bet },
      ],
    });
    toast.success(`DD ticket archived — race ${race || "?"} · $${cost}`, {
      duration: 3500,
    });
  };

  if (!handicapped) return null;

  return (
    <section className="mt-10 mx-auto max-w-3xl" data-testid="optimal-ticket">
      <div className="flex items-baseline justify-between mb-4 px-1 flex-wrap gap-3">
        <h2
          className="text-2xl text-[#f5ecd6]"
          style={{
            fontFamily: "'Instrument Serif', 'Times New Roman', serif",
            fontWeight: 400,
            letterSpacing: "-0.01em",
          }}
          data-testid="ticket-title"
        >
          Optimal <em className="text-[#84cc16]">Daily Double</em> Ticket{" "}
          <PanelHelp step={4} label="the Daily Double ticket" />
        </h2>
        <div className="flex items-center gap-4 flex-wrap">
          <BaseSelector value={base} onChange={setBase} />
          <span
            className="text-[11px] uppercase tracking-[0.2em] text-stone-500"
            style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
            data-testid="ticket-entries"
          >
            {leftValid.length} L · {rightValid.length} R valid
          </span>
        </div>
      </div>

      <div
        data-testid="dd-structure-selector"
        className="mb-3 px-1 flex items-center gap-2 flex-wrap"
      >
        <span
          className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mr-1"
          style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
        >
          Structure
        </span>
        {STRUCTURES.map((s) => {
          const active = s.key === structure.key;
          return (
            <button
              key={s.key}
              data-testid={`dd-structure-${s.key}`}
              onClick={() => setStructureKey(s.key)}
              className={`px-3 h-8 text-[11px] font-bold tracking-[0.14em] uppercase rounded-md border transition-colors ${
                active
                  ? "bg-[#84cc16] text-[#0a0a0a] border-[#365314]"
                  : "bg-transparent text-stone-300 border-stone-700 hover:border-stone-500"
              }`}
              style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
            >
              {s.label}
            </button>
          );
        })}
        {(leg2LockCount > 0 || (elimSet && elimSet.size > 0)) && (
          <span
            data-testid="dd-lock-summary"
            className="ml-auto inline-flex items-center gap-2 px-2 h-7 rounded text-[10px] tracking-[0.18em] uppercase font-bold"
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              background: "#141414",
              color: "#f5ecd6",
            }}
          >
            {elimSet && elimSet.size > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[#f87171]"
                title="Eliminated horses"
                data-testid="dd-elim-count"
              >
                <X size={11} strokeWidth={3} /> {elimSet.size}
              </span>
            )}
            {leg2LockCount > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[#facc15]"
                title="Leg 2 locked posts"
                data-testid="dd-leg2-lock-count"
              >
                <Pin size={11} strokeWidth={3} /> L2 · {leg2LockCount}
              </span>
            )}
          </span>
        )}
      </div>

      <div
        data-testid="card-dd"
        className="rounded-lg overflow-hidden ring-1 ring-stone-700/50"
        style={{ background: "#141414" }}
      >
        <header className="px-5 py-3 border-b border-stone-700/60 flex items-baseline justify-between gap-3 flex-wrap">
          <div className="flex items-baseline gap-3">
            <span
              className="text-[10px] uppercase tracking-[0.28em] px-2 py-0.5 rounded"
              style={{
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                background: "#84cc16",
                color: "#0a0a0a",
                fontWeight: 700,
              }}
            >
              Daily Double
            </span>
            <h3
              className="text-xl text-[#f5ecd6]"
              style={{ fontFamily: "'Instrument Serif', 'Times New Roman', serif", fontWeight: 400 }}
            >
              {leg1Bet.length}-with-{leg2Bet.length} wheel
            </h3>
            <EditableTargetBadge
              track={trackName}
              trackSlug={trackSlug}
              race={race}
              tracks={tracks}
              onEdit={onEditTarget}
              testId="dd-target-badge"
            />
          </div>
          <div className="flex items-baseline gap-4 flex-wrap">
            <span
              data-testid="card-dd-count"
              className="text-[11px] uppercase tracking-[0.2em]"
              style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
            >
              <span className="text-stone-500">Combos: </span>
              <span className="text-[#f5ecd6] font-bold">{combos}</span>
            </span>
            <span
              data-testid="card-dd-cost"
              className="text-[11px] uppercase tracking-[0.2em]"
              style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
            >
              <span className="text-stone-500">Cost: </span>
              <span className="text-[#84cc16] font-bold">${cost}</span>
              <span className="text-stone-600"> @ ${base.toFixed(2)}</span>
            </span>
            {!tooLarge && !tooFew && combos > 0 && (
              <button
                data-testid="dd-archive-btn"
                onClick={archive}
                className="inline-flex items-center gap-1.5 px-3 h-7 rounded-md text-[10px] font-bold tracking-[0.18em] uppercase transition-all bg-transparent text-[#84cc16] border border-[#365314] hover:bg-[#84cc16] hover:text-[#0a0a0a]"
                style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                title="Save this DD ticket to History"
              >
                <Archive size={12} />
                Archive
              </button>
            )}
          </div>
        </header>

        {tooLarge ? (
          <div
            data-testid="dd-toolarge"
            className="px-6 py-8 text-[#fde047] text-sm leading-relaxed"
            style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
          >
            Field too large for reliable optimal ticket ({leftValid.length} left ·{" "}
            {rightValid.length} right). Cybertote consolidates DD willpays
            into 10 betting interests for 11+ horse fields, producing
            duplicated DDPO values. No ticket generated.
          </div>
        ) : tooFew ? (
          <div
            data-testid="dd-toofew"
            className="px-6 py-8 text-stone-400 text-sm leading-relaxed"
            style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
          >
            Need at least {structure.leg1} valid entries in the left half (DD & ML) and {structure.leg2} in
            the right half (SLLWP & SLML) to build the Daily Double ticket.
            Currently: {leftValid.length} left · {rightValid.length} right ({totalValid} total).
          </div>
        ) : (
          <div className="p-5 space-y-2">
            <StrikeHint count={struckCount} onClear={strikes.clear} testId="dd-strike-reset" />
            {tooManyLeg2Locks && (
              <div
                data-testid="dd-locks-overflow"
                className="text-[10px] uppercase tracking-[0.18em] text-[#facc15] pb-1"
                style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
              >
                Leg 2 has {leg2LockCount} locks but structure only fits{" "}
                {structure.leg2}. Extra locks are ignored — pick a bigger
                structure or unlock some.
              </div>
            )}
            <Line
              prefix="leg1"
              label="1st Leg"
              sublabel={`Top ${structure.leg1} · IQ Power rank${
                elimSet && elimSet.size > 0 ? ` · ${elimSet.size} eliminated` : ""
              }`}
              horses={leg1}
              locks={null}
              tone="lime"
              strikes={strikes}
              slot="leg1"
            />
            <WithDivider />
            <Line
              prefix="leg2"
              label="2nd Leg"
              sublabel={`Top ${structure.leg2} · IQ Power rank${
                leg2LockCount > 0 ? ` · ${Math.min(leg2LockCount, structure.leg2)} locked` : ""
              }`}
              horses={leg2}
              locks={leg2Locks}
              tone="cream"
              strikes={strikes}
              slot="leg2"
            />
          </div>
        )}
      </div>
    </section>
  );
};
